import { queryOptions, useMutation, useQuery, useQueryClient, QueryClient } from "@tanstack/react-query";
import { parseListResponse, parseResponse } from "lib/fetch";
import { AdApplicationInterface, AdInterface } from "types/ad";
import {
    applyToOurAdRequest,
    confirmAdApplyRequest,
    createAdRequest,
    deleteAdRequest,
    getAdApplicationsRequest,
    getAdRequest,
    getAdsRequest,
    getOurAdRequest,
    getOurAdApplicationsRequest,
    getOurAdsRequest,
    patchAdRequest,
    refuseAdApplyRequest,
    updateAdRequest,
} from "lib/api/ad";

export const fetchAds = (params?: HashMap<unknown>) =>
    parseListResponse<AdInterface>(getAdsRequest(params));

export const fetchAdApplications = (id: string) =>
    parseListResponse<AdApplicationInterface>(getAdApplicationsRequest(id));

export const fetchAd = (id: string) => parseResponse<AdInterface>(getAdRequest(id));

export const createAd = (data: HashMap<unknown>) => parseResponse<AdInterface>(createAdRequest(data));

export const updateAd = (id: string, data: HashMap<unknown>) =>
    parseResponse<AdInterface>(updateAdRequest(id, data));

export const deleteAd = (id: string) => parseResponse<AdInterface>(deleteAdRequest(id));

export const patchAd = (id: string, data: HashMap<unknown>) =>
    parseResponse<AdInterface>(patchAdRequest(id, data));

export const confirmAdApply = (adId: string, data: HashMap<unknown>) =>
    parseResponse<AdInterface>(confirmAdApplyRequest(adId, data));

export const refuseAdApply = (adId: string, data: HashMap<unknown>) =>
    parseResponse<AdInterface>(refuseAdApplyRequest(adId, data));

export const fetchOurAds = (params?: HashMap<unknown>) =>
    parseListResponse<AdInterface>(getOurAdsRequest(params));

export const fetchOurAd = (id: string) => parseResponse<AdInterface>(getOurAdRequest(id));

export const applyToOurAd = (adId: string, serviceId: string, data: HashMap<unknown>) =>
    parseResponse<AdApplicationInterface>(applyToOurAdRequest(adId, serviceId, data));

export const fetchOurAdApplications = () =>
    parseListResponse<AdApplicationInterface>(getOurAdApplicationsRequest());

export const adKeys = {
    all: ["ad"] as const,
    lists: () => [...adKeys.all, "list"] as const,
    details: () => [...adKeys.all, "detail"] as const,
    detail: (id: string) => [...adKeys.details(), id] as const,
    applications: () => [...adKeys.all, "applications"] as const,
    application: (id: string) => [...adKeys.applications(), id] as const,
    ourLists: () => [...adKeys.all, "our-list"] as const,
    ourDetails: () => [...adKeys.all, "our-detail"] as const,
    ourDetail: (id: string) => [...adKeys.ourDetails(), id] as const,
    ourApplications: () => [...adKeys.all, "our-applications"] as const,
};

export const adDetailQueryOptions = (id: string | undefined) =>
    queryOptions<AdInterface>({
        queryKey: id ? adKeys.detail(id) : adKeys.detail("__missing__"),
        queryFn: () => {
            if (!id) {
                return Promise.reject(new Error("Missing ad id"));
            }
            return fetchAd(id);
        },
        enabled: Boolean(id),
    });

export const useAd = (id: string | undefined) => useQuery(adDetailQueryOptions(id));

export const ourAdDetailQueryOptions = (id: string | undefined) =>
    queryOptions<AdInterface>({
        queryKey: id ? adKeys.ourDetail(id) : adKeys.ourDetail("__missing__"),
        queryFn: () => {
            if (!id) {
                return Promise.reject(new Error("Missing ad id"));
            }
            return fetchOurAd(id);
        },
        enabled: Boolean(id),
    });

export const useOurAd = (id: string | undefined) => useQuery(ourAdDetailQueryOptions(id));

export const useDeleteAd = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteAd(id),
        onSuccess: (_data, id) => {
            queryClient.invalidateQueries({ queryKey: adKeys.lists() });
            queryClient.invalidateQueries({ queryKey: adKeys.detail(id) });
            queryClient.invalidateQueries({ queryKey: adKeys.application(id) });
        },
    });
};

export const useCreateAd = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: HashMap<unknown>) => createAd(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: adKeys.lists() });
        },
    });
};

export const useUpdateAd = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: HashMap<unknown> }) => updateAd(id, data),
        onSuccess: (_response, { id }) => {
            queryClient.invalidateQueries({ queryKey: adKeys.lists() });
            queryClient.invalidateQueries({ queryKey: adKeys.detail(id) });
            queryClient.invalidateQueries({ queryKey: adKeys.application(id) });
        },
    });
};

export const usePatchAd = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: HashMap<unknown> }) => patchAd(id, data),
        onSuccess: (_response, { id }) => {
            queryClient.invalidateQueries({ queryKey: adKeys.detail(id) });
            queryClient.invalidateQueries({ queryKey: adKeys.application(id) });
        },
    });
};

export const useRefuseAdApply = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ adId, data }: { adId: string; data: HashMap<unknown> }) => refuseAdApply(adId, data),
        onSuccess: (_response, { adId }) => {
            queryClient.invalidateQueries({ queryKey: adKeys.detail(adId) });
            queryClient.invalidateQueries({ queryKey: adKeys.application(adId) });
            queryClient.invalidateQueries({ queryKey: adKeys.ourApplications() });
        },
    });
};

export const useConfirmAdApply = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ adId, data }: { adId: string; data: HashMap<unknown> }) => confirmAdApply(adId, data),
        onSuccess: (_response, { adId }) => {
            queryClient.invalidateQueries({ queryKey: adKeys.detail(adId) });
            queryClient.invalidateQueries({ queryKey: adKeys.application(adId) });
            queryClient.invalidateQueries({ queryKey: adKeys.ourApplications() });
        },
    });
};

export const useApplyToOurAd = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ adId, serviceId, data }: { adId: string; serviceId: string; data: HashMap<unknown> }) =>
            applyToOurAd(adId, serviceId, data),
        onSuccess: (_response, { adId }) => {
            queryClient.invalidateQueries({ queryKey: adKeys.ourLists() });
            queryClient.invalidateQueries({ queryKey: adKeys.ourDetail(adId) });
            queryClient.invalidateQueries({ queryKey: adKeys.ourApplications() });
        },
    });
};

export const useOurAdApplications = (enabled = true) =>
    useQuery({
        queryKey: adKeys.ourApplications(),
        queryFn: fetchOurAdApplications,
        enabled,
    });

export const useAdApplications = (adId: string | undefined) =>
    useQuery({
        queryKey: adId ? adKeys.application(adId) : adKeys.application("__missing__"),
        queryFn: () => {
            if (!adId) return Promise.reject(new Error("Missing ad id"));
            return fetchAdApplications(adId);
        },
        enabled: Boolean(adId),
    });

export const prefetchAd = (queryClient: QueryClient, id: string) =>
    queryClient.prefetchQuery(adDetailQueryOptions(id));
